// Libs
import classNames from 'classnames/bind';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { useCallback, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  IconButton,
  Stack,
} from '@mui/material';
// Components, Layouts, Pages
import { BaseChoose, BaseInput, BaseLabel, BaseSelect, Loading } from '~/components/common';
// Others
import { useAppDispatch } from '~/utils/redux/hooks';
import { IFormIncident } from '~/utils/interface/incident';
import { optionsTypeIncident, optionsTypePriority } from '~/utils/constants/common';
import { IBaseOption, IResultCallApiFn } from '~/utils/interface/common';
import { defaultValuesIncidentForm, schema } from './helper';
import { createIncident } from '~/thunks/incident/incidentSlice';
import { incidentActions } from '~/thunks/incident/incidentThunk';
// Styles, images, icons
import styles from './IncidentForm.module.scss';
import { icons } from '~/assets';

type Props = {
  onClose: () => void;
};

const cx = classNames.bind(styles);

const IncidentForm = (props: Props) => {
  //#region Destructuring Props
  const { onClose } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/jobs', 'common/base']);
  const { id: jobId } = useParams();
  const dispatch = useAppDispatch();
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<IFormIncident>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesIncidentForm,
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleAddForm = (data: IFormIncident) => {
    const convertData: IFormIncident = {
      ...data,
      jobId: jobId,
    };

    setIsLoading(true);

    dispatch(createIncident(convertData))
      .unwrap()
      .then((res) => {
        handleCloseModal();
        dispatch(incidentActions.setRefreshIncidentList(true));
      })
      .catch((err) => {})
      .finally(() => setIsLoading(false));
  };

  const handleCloseModal = () => {
    onClose && onClose();
    reset();
  };

  const fetchDataPriority = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionsTypePriority,
      hasMoreResp: false,
    };

    return result;
  }, []);
  //#endregion Handle Function

  return (
    <Dialog
      id='incidentFormModalComponent'
      aria-labelledby='incidentFormModal'
      open={true}
      onClose={handleCloseModal}
      fullWidth
      maxWidth={'md'}
    >
      <DialogTitle id='incidentFormModal' className={cx('header')}>
        <Stack direction={'column'} spacing={'10px'}>
          <Stack direction={'row'} spacing={10}>
            <BaseLabel label={t('job_detail_title_log_new_incident')} styleType='bigTitle' />
          </Stack>
        </Stack>

        <IconButton onClick={handleCloseModal} className={cx('buttonClose')}>
          <Avatar
            src={icons.commonIconCancel}
            className={cx('iconClose')}
            alt={t('common_alt_img', { ns: 'common/base' })}
          />
        </IconButton>
      </DialogTitle>

      <DialogContent className={cx('content')} dividers>
        <Grid2 columnSpacing={'24px'} rowSpacing={'24px'} container>
          <Grid2 size={12}>
            <Controller
              control={control}
              name={'type'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseChoose
                    label={t('job_detail_title_incident_type')}
                    value={value}
                    option={optionsTypeIncident}
                    onChange={(data) => onChange(data?.value)}
                    errorMessage={errors?.type?.message}
                    required
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name={'priority'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseSelect
                    gap={10}
                    height={36}
                    styleType='contained'
                    label={t('job_detail_title_priority')}
                    callApiFn={fetchDataPriority}
                    value={value}
                    onChange={(data) => onChange(data?.value)}
                    errorMessage={errors?.priority?.message}
                    required
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={12}>
            <Controller
              control={control}
              name={'description'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseInput
                    multiline
                    rows={7}
                    height={'auto'}
                    label={t('job_detail_title_description')}
                    value={value}
                    onChange={onChange}
                    errorMessage={errors?.description?.message}
                  />
                );
              }}
            />
          </Grid2>
        </Grid2>
      </DialogContent>

      <DialogActions className={cx('groupButton')}>
        <Button variant='outlined' className={cx('buttonCancel')} onClick={handleCloseModal}>
          {t('common_action_cancel', { ns: 'common/base' })}
        </Button>

        <Button variant='contained' className={cx('buttonSave')} onClick={handleSubmit(handleAddForm)}>
          {t('common_action_save', { ns: 'common/base' })}
        </Button>
      </DialogActions>

      {isLoading && <Loading />}
    </Dialog>
  );
};

export default IncidentForm;
